import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { NgIf, NgClass, AsyncPipe, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from '../../../state/reducers';
import * as VeridionExportActions from '../../../state/veridionExport/veridion-export.actions';
import * as VeridionExportSelectors from '../../../state/veridionExport/veridion-export.selectors';
import { ExportValidation, StartExportResponse } from '../../../services/veridion-export.service';

type ModalStep = 'validating' | 'confirmation' | 'processing' | 'success' | 'error' | 'insufficient_credits';

@Component({
  selector: 'app-export-modal',
  standalone: true,
  imports: [NgIf, NgClass, AsyncPipe, DecimalPipe, RouterModule],
  templateUrl: './export-modal.component.html',
  styleUrl: './export-modal.component.scss'
})
export class ExportModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible = false;
  @Input() totalRecords = 0;
  @Input() filters: any[] = [];
  @Output() modalClosed = new EventEmitter<void>();

  currentStep: ModalStep = 'validating';
  errorMessage = '';

  // Observables from store
  validation$: Observable<ExportValidation | null>;
  validationLoading$: Observable<boolean>;
  validationError$: Observable<string | null>;
  startExportLoading$: Observable<boolean>;
  startExportResult$: Observable<StartExportResponse | null>;
  startExportError$: Observable<string | null>;

  private destroy$ = new Subject<void>();

  constructor(private store: Store<AppState>) {
    this.validation$ = this.store.select(VeridionExportSelectors.selectValidationResult);
    this.validationLoading$ = this.store.select(VeridionExportSelectors.selectValidationLoading);
    this.validationError$ = this.store.select(VeridionExportSelectors.selectValidationError);
    this.startExportLoading$ = this.store.select(VeridionExportSelectors.selectStartExportLoading);
    this.startExportResult$ = this.store.select(VeridionExportSelectors.selectStartExportResult);
    this.startExportError$ = this.store.select(VeridionExportSelectors.selectStartExportError);

    // Subscribe to state changes to update modal step
    this.setupSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible) {
      // Reset and trigger validation when modal opens
      this.currentStep = 'validating';
      this.errorMessage = '';
      this.store.dispatch(VeridionExportActions.resetValidationState());
      this.store.dispatch(VeridionExportActions.resetStartExportState());

      if (this.totalRecords > 0) {
        this.store.dispatch(VeridionExportActions.validateExportCredits({
          totalRecords: this.totalRecords
        }));
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions(): void {
    // Handle validation result
    this.validation$.pipe(takeUntil(this.destroy$)).subscribe(validation => {
      if (validation && this.currentStep === 'validating') {
        if (validation.can_export) {
          this.currentStep = 'confirmation';
        } else {
          this.currentStep = 'insufficient_credits';
          this.errorMessage = validation.message || 'Insufficient credits for this export.';
        }
      }
    });

    // Handle validation error
    this.validationError$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error && this.currentStep === 'validating') {
        this.currentStep = 'error';
        this.errorMessage = error;
      }
    });

    // Handle export success
    this.startExportResult$.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result && this.currentStep === 'processing') {
        this.currentStep = 'success';
      }
    });

    // Handle export error
    this.startExportError$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error && this.currentStep === 'processing') {
        this.currentStep = 'error';
        this.errorMessage = error;
      }
    });
  }

  onProceed(): void {
    this.currentStep = 'processing';
    this.store.dispatch(VeridionExportActions.startVeridionExport({
      filters: this.filters,
      totalRecords: this.totalRecords
    }));
  }

  onRetry(): void {
    this.currentStep = 'validating';
    this.errorMessage = '';
    this.store.dispatch(VeridionExportActions.resetValidationState());

    if (this.totalRecords > 0) {
      this.store.dispatch(VeridionExportActions.validateExportCredits({
        totalRecords: this.totalRecords
      }));
    }
  }

  close(): void {
    this.isVisible = false;
    this.store.dispatch(VeridionExportActions.resetValidationState());
    this.store.dispatch(VeridionExportActions.resetStartExportState());
    this.modalClosed.emit();
  }
}
